import styled from "@emotion/styled";
import { useState, useRef } from "react";
import { COLORS, dateFormatter, priceFormatter } from "../../settings/themeSettings";
import { THEME_COLORS } from "../../constants/themeColors";
import { useTableAlignment } from "../../hooks/useTableAlignment";
import "./styledTable.scss";
import {
  TablePagination,
  tablePaginationClasses as classes,
} from '@mui/base/TablePagination';
import { Tooltip } from "@mui/material";
import { Info } from "@mui/icons-material";


// Couleurs du thème — conservées pour usage futur
// eslint-disable-next-line no-unused-vars
const themeColors = {
  primary: THEME_COLORS.PRIMARY,
  primaryDark: THEME_COLORS.PRIMARY_DARK,
  primaryLight: THEME_COLORS.PRIMARY_LIGHT,
  white: THEME_COLORS.WHITE,
};

// eslint-disable-next-line no-unused-vars
const grey = {
  50: '#F3F6F9',
  100: '#E7EBF0',
  200: '#E0E3E7',
  300: '#CDD2D7',
  400: '#B2BAC2',
  500: '#A0AAB4',
  600: '#6F7E8C',
  700: '#3E5060',
  800: '#2D3843',
  900: '#1A2027',
};



const nameFormatter = (name) => {
  const underscoreRegex = new RegExp("_");
  const numeroRegex = new RegExp("numero");
  if (name === "createdAt") {
    return "Date";
  }
  return name.replace(underscoreRegex, " ").replace(numeroRegex, "N°").replaceAll(/([A-Z])/g, ' $1').trim();
}

const Root = styled('div')(
  () => `
    position: relative;
    flex: 1;
    overflow-y: auto;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid ${COLORS.gray[200]};
    margin: 0 20px 20px 20px;

    table {
      font-family: "Inter", "Roboto", sans-serif;
      font-size: 0.7rem; /* Réduit de 0.75rem */
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
      background-color: ${COLORS.background.paper};
      table-layout: fixed; /* Optimisation largeur colonnes */
    }
  
    td,
    th {
      text-align: left;
      padding: 4px 8px; /* Réduit de 6px 12px */
      color: ${COLORS.text.primary};
      text-transform: capitalize;
      border-bottom: 1px solid ${COLORS.gray[200]};
      transition: all 0.2s ease-in-out;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Largeurs ajustées pour plus de compacité */
    .col-date { width: 90px; }
    .col-number { width: 60px; }
    .col-price { width: 90px; }
    .col-text { width: 150px; }
    .col-id { width: 50px; }
    .col-actions { width: 80px; }
  
    th {
      background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%);
      color: ${COLORS.text.white};
      font-weight: 700;
      font-size: 0.65rem; /* Plus petit pour les en-têtes */
      letter-spacing: 0.01em;
      text-transform: uppercase;
      position: sticky;
      top: 0;
      z-index: 20;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      
      &:first-of-type {
        border-top-left-radius: 12px;
      }
      
      &:last-of-type {
        border-top-right-radius: 12px;
      }
    }
    
    tbody tr {
      transition: all 0.2s ease-in-out;
      
      &:hover {
        background-color: ${COLORS.gray[50]};
        transform: translateY(-1px);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }
      
      &:last-child td {
        border-bottom: none;
      }
    }
    
    td {
      font-weight: 500;
      
      &:first-of-type {
        font-weight: 600;
        color: ${COLORS.primary};
      }
    }
    `,
);

const CustomTablePagination = styled(TablePagination)(
  () => `
    & .${classes.spacer} {
      display: none;
    }
  
    & .${classes.toolbar}  {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      padding: 16px 24px;
      background-color: ${COLORS.background.paper};
      border-top: 1px solid ${COLORS.gray[200]};
      color: ${COLORS.text.primary};
  
      @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
      }
    }
  
    & .${classes.selectLabel} {
      color: ${COLORS.text.secondary};
      margin: 0;
      font-weight: 500;
      font-size: 0.875rem;
    }
  
    & .${classes.select}{
      padding: 8px 12px;
      border: 1px solid ${COLORS.gray[300]};
      border-radius: 8px;
      background-color: ${COLORS.background.paper};
      color: ${COLORS.text.primary};
      font-weight: 500;
      transition: all 0.2s ease-in-out;
  
      &:hover {
        border-color: ${COLORS.primary};
        background-color: ${COLORS.gray[50]};
      }
  
      &:focus {
        outline: none;
        border-color: ${COLORS.primary};
        box-shadow: 0 0 0 3px ${COLORS.primary}20;
        background-color: ${COLORS.background.paper};
      }
    }
  
    & .${classes.displayedRows} {
      margin: 0;
      color: ${COLORS.text.secondary};
      font-weight: 500;
      font-size: 0.875rem;
      @media (min-width: 768px) {
        margin-left: auto;
      }
    }
  
    & .${classes.actions} {
      padding: 4px;
      border: 1px solid ${COLORS.gray[300]};
      border-radius: 8px;
      text-align: center;
      background-color: ${COLORS.background.paper};
    }
  
    & .${classes.actions} > button {
      margin: 0 4px;
      border: none;
      border-radius: 6px;
      background-color: transparent;
      color: ${COLORS.text.secondary};
      padding: 8px;
      transition: all 0.2s ease-in-out;
      
      & > * {
        color: ${COLORS.text.secondary};
      }

      &:hover {
        background-color: ${COLORS.primary};
        color: ${COLORS.text.white};
        
        & > * {
          color: ${COLORS.text.white};
        }
      }
  
      &:focus {
        outline: none;
        background-color: ${COLORS.primary};
        color: ${COLORS.text.white};
        
        & > * {
          color: ${COLORS.text.white};
        }
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        
        &:hover {
          background-color: transparent;
          color: ${COLORS.text.secondary};
          
          & > * {
            color: ${COLORS.text.secondary};
          }
        }
      }
    }
    `,
);




const StyledTable = ({ head = [], rows = [], onOptionClicked, onRowDoubleClick, headLabels = {}, customRenderers = {}, hideOptions = false, disableScroll = false }) => {

  // Masquer la colonne Options si hideOptions est true OU si onOptionClicked n'est pas fourni
  const showOptions = !hideOptions && typeof onOptionClicked === 'function';

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const tableRef = useRef(null);

  const handleRowDoubleClick = (row) => {
    if (onRowDoubleClick) {
      onRowDoubleClick(row.id, row);
    } else if (onOptionClicked) {
      // UX-08 : Par défaut, le double-clic fait la même chose que le clic sur options (ouvrir les détails/édition)
      onOptionClicked(row.id, row);
    }
  };

  // Application automatique des règles d'alignement
  useTableAlignment(tableRef, rows, {
    autoAlign: true,
    autoColor: true,
    autoWidth: true,
    applyHover: true
  });


  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (
    event,
    newPage,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };





  const getColumnClass = (title) => {
    if (/date|createdAt/i.test(title)) return 'col-date';
    if (/montant|prix|tarif|depouillement|marge/i.test(title)) return 'col-price';
    if (/pax|nuitee|nombre|jour/i.test(title)) return 'col-number';
    if (title === 'id' || title === '_id') return 'col-id';
    return 'col-text';
  };

  return <Root sx={{ width: '100%', maxWidth: '100%', ...(disableScroll ? { maxHeight: 'none', overflowY: 'visible', boxShadow: 'none', border: 'none' } : {}) }} ref={tableRef}>
    <table aria-label="custom pagination table">
      <thead>
        <tr>
          {
            head.map((title, idx) => {
              const label = nameFormatter(headLabels.hasOwnProperty(title) ? headLabels[title] : title);
              return (
                <th key={idx} className={getColumnClass(title)} style={{ textAlign: 'center' }}>
                  <Tooltip title={label} arrow>
                    <span>{label}</span>
                  </Tooltip>
                </th>
              );
            })
          }
          {showOptions && <th className="col-actions" style={{ textAlign: 'center' }}>Options </th>}
        </tr>
      </thead>
      <tbody>
        {(rowsPerPage > 0
          ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : rows
        ).map((row, idx) => (
          <tr 
            key={idx} 
            onClick={() => onOptionClicked && onOptionClicked(row.id, row)}
            onDoubleClick={() => handleRowDoubleClick(row)}
            style={{ cursor: (onOptionClicked || onRowDoubleClick) ? 'pointer' : 'default' }}
          >
            {
              head.map((title, titleIdx) => {
                const value = customRenderers[title] ? customRenderers[title](row[title], row) : (/montant|prix|tarif|depouillement|marge$/.test(title) ? priceFormatter(row[title]) : ((/date|createdAt/.test(title)) ? dateFormatter(row[title]) : row[title]));
                return (
                  <td key={titleIdx} className={getColumnClass(title)} align="right">
                    <Tooltip title={String(value || "")} arrow>
                      <span>{value}</span>
                    </Tooltip>
                  </td>
                );
              })
            }



            {showOptions && <td className="col-actions" style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: '12px',
              border: 'none'
            }}>
              <div style={{
                padding: '8px',
                borderRadius: '8px',
                backgroundColor: `${COLORS.primary}15`,
                transition: 'all 0.2s ease-in-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = `${COLORS.primary}25`;
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = `${COLORS.primary}15`;
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <Info
                  onClick={() => onOptionClicked(row.id)}
                  style={{
                    fill: COLORS.primary,
                    fontSize: '1.25rem',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </td>}
          </tr>
        ))}

        {emptyRows > 0 && (
          <tr style={{ height: 34 * emptyRows }}>
            <td colSpan={3} />
          </tr>
        )}
      </tbody>
      <tfoot>
        <tr>
          <CustomTablePagination
            rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
            // colSpan={3}
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            labelRowsPerPage={"lignes par page"}
            slotProps={{
              select: {
                'aria-label': 'rows per page',
              },
              actions: {
                showFirstButton: true,
                showLastButton: true,
              },
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </tr>
      </tfoot>
    </table>
  </Root>
}


export default StyledTable;
